import {
	FilterOperator,
	type FindAllResponse,
	type UseCaseCriteria
} from '@contexts/shared/domain';

export function findAllEntities<T extends Record<string, unknown>>(
	entities: T[],
	criteria?: UseCaseCriteria
): FindAllResponse<T> {
	let results = [...entities];

	// Aplicar filtros
	if (criteria?.filters) {
		results = results.filter((entity) =>
			criteria.filters?.every((filter) => {
				const entityValue = entity[filter.field as keyof T];
				switch (filter.operator) {
					case FilterOperator.EQUAL:
						return entityValue === filter.value;
					case FilterOperator.NE:
						return entityValue !== filter.value;
					case FilterOperator.GT:
						return (
							typeof entityValue === 'number' &&
							entityValue > (filter.value as number)
						);
					case FilterOperator.GTE:
						return (
							typeof entityValue === 'number' &&
							entityValue >= (filter.value as number)
						);
					case FilterOperator.LT:
						return (
							typeof entityValue === 'number' &&
							entityValue < (filter.value as number)
						);
					case FilterOperator.LTE:
						return (
							typeof entityValue === 'number' &&
							entityValue <= (filter.value as number)
						);
					case FilterOperator.NULL:
						return entityValue === null || entityValue === undefined;
					default:
						return false;
				}
			})
		);
	}

	// Aplicar búsqueda
	if (criteria?.search) {
		const searchLower = criteria.search.toLowerCase();
		results = results.filter((entity) =>
			Object.values(entity).some(
				(value) => typeof value === 'string' && value.toLowerCase().includes(searchLower)
			)
		);
	}

	// Aplicar ordenación
	if (criteria?.orderBy) {
		results.sort((a, b) => {
			for (const order of criteria.orderBy) {
				const valueA = a[order.field as keyof T];
				const valueB = b[order.field as keyof T];
				if (valueA < valueB) return order.sortDirection === 'asc' ? -1 : 1;
				if (valueA > valueB) return order.sortDirection === 'asc' ? 1 : -1;
			}
			return 0;
		});
	}

	// Aplicar paginación
	const pageSize = criteria?.pageSize ?? results.length;
	const page = criteria?.page ?? 1;
	const total = results.length ?? 0;
	const pages = total === 0 ? 1 : Math.ceil(total / pageSize);
	const paginatedResults = results.slice((page - 1) * pageSize, page * pageSize);

	return {
		total,
		items: paginatedResults,
		page,
		pages
	};
}
